import { useEffect, useRef, useState } from "react";
import { categoryLabel } from "../lib/allocations";
import { ArrowDownLeft, ArrowLeftRight, Handshake, MapPin, Percent, Plane, Tag, Users } from "lucide-react";
import type { Account, Payee, Transaction, Trip } from "../types";
import { isExpense, isIncome, markupAmount, money, shortName } from "../lib/finance";
import { categoryIcon } from "./CategoryIcon";
import Avatar from "./Avatar";

export interface TransactionRowProps {
  t: Transaction;
  accounts: Account[];
  payees?: Payee[];
  trips?: Trip[];
  tripName?: string;
  hideTripBadge?: boolean;
  showDestinationOnly?: boolean;
  trackedCount?: number;
  amountBasis?: "actual" | "reportable";
  perspectiveAccountId?: string;
  onClick: () => void;
}

export default function TransactionRow({
  t,
  accounts,
  payees,
  trips,
  tripName,
  hideTripBadge = false,
  showDestinationOnly = false,
  trackedCount = 0,
  amountBasis = "actual",
  perspectiveAccountId,
  onClick,
}: TransactionRowProps) {
  const [showTip, setShowTip] = useState(false);
  const timerRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleBadgeClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowTip((prev) => !prev);
    timerRef.current = window.setTimeout(() => {
      setShowTip(false);
    }, 2800);
  };

  const handleMouseEnter = () => {
    setShowTip(true);
  };

  const handleMouseLeave = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    setShowTip(false);
  };

  const account = accounts.find((a) => a.id === t.accountId);
  const destinationAccount = accounts.find((a) => a.id === t.toAccountId);
  const destinationOwner = t.toOwner || destinationAccount?.owner;
  const titleText = t.title || t.description || categoryLabel(t);
  const payeeName = t.payeeId ? payees?.find((p) => p.id === t.payeeId)?.name : t.draftPayeeName;
  const showPayeeInMeta = Boolean(
    payeeName && payeeName.trim().toLowerCase() !== titleText.trim().toLowerCase(),
  );
  const isIncomingTransfer = Boolean(
    perspectiveAccountId &&
      t.toAccountId === perspectiveAccountId &&
      t.accountId !== perspectiveAccountId &&
      t.receivedAmount !== undefined,
  );
  const displayCurrency = isIncomingTransfer
    ? t.receivedCurrency || destinationAccount?.currency || t.currency
    : t.currency;
  const baseAmount = isIncomingTransfer ? t.receivedAmount! : t.amount;

  const expense = isExpense(t),
    income = isIncome(t);
  const amount = amountBasis === "reportable" && (expense || income) && !t.toAccountId
    ? t.reportableAmount ?? t.amount
    : baseAmount;
  const showTotal = amount !== baseAmount;
  const markup = Number(t.markup?.replace(/%$/, ""));
  const markupLabel = Number.isFinite(markup) && markup > 0 ? `Markup applied: ${markup}%` : "";
  const markupFee = markupAmount(baseAmount, t.markup);
  const trackedLabel = `${trackedCount} tracked ${trackedCount === 1 ? "item" : "items"}`;
  const Icon = expense
    ? categoryIcon(t.category)
    : income
      ? ArrowDownLeft
      : ArrowLeftRight;

  const isTripTx = Boolean(t.tripId);
  const matchedTrip = isTripTx && trips ? trips.find((tr) => tr.id === t.tripId) : undefined;
  const resolvedTripName = tripName || matchedTrip?.name;
  const showTripBadge = isTripTx && !hideTripBadge;

  let badgeIcon = <Plane size={11} strokeWidth={2.2} />;
  let badgeTooltip = "";

  if (showTripBadge) {
    if (showDestinationOnly && t.tripDestination) {
      badgeIcon = <MapPin size={11} strokeWidth={2.2} />;
      badgeTooltip = resolvedTripName
        ? `${resolvedTripName} · ${t.tripDestination}`
        : t.tripDestination;
    } else if (!showDestinationOnly) {
      badgeIcon = <Plane size={11} strokeWidth={2.2} />;
      if (resolvedTripName) {
        badgeTooltip =
          t.tripDestination && t.tripDestination !== resolvedTripName
            ? `${resolvedTripName} (${t.tripDestination})`
            : resolvedTripName;
      } else {
        badgeTooltip = t.tripDestination || "Trip transaction";
      }
    }
  }

  const displayedAmount = `${amount === 0 ? "" : expense ? "−" : income ? "+" : ""}${money(amount, displayCurrency).replace(` ${displayCurrency}`, "")}`;
  const primaryAmount = (
    <strong title={showTotal ? "Reportable amount" : undefined} aria-label={showTotal ? `Reportable amount ${displayedAmount} ${displayCurrency}` : undefined}>
      {displayedAmount}
    </strong>
  );

  return (
    <button className="transaction-row" onClick={onClick}>
      <span className={"transaction-icon " + (!expense ? "transfer" : "")}>
        <Icon size={20} strokeWidth={1.65} />
      </span>
      <span className="transaction-description">
        <span className="transaction-row-top">
          <strong className="transaction-title">{t.title || t.description || categoryLabel(t)}</strong>
          {markupLabel && (
            <span className="transaction-indicator transaction-markup" role="img" title={markupLabel} aria-label={markupLabel}>
              <Percent size={13} strokeWidth={2} aria-hidden="true" />
            </span>
          )}
          {trackedCount > 0 && (
            <span className="transaction-indicator transaction-tracked" role="img" title={trackedLabel} aria-label={trackedLabel}>
              <Tag size={13} strokeWidth={2} aria-hidden="true" />
              <span className="transaction-tracked-count" aria-hidden="true">{trackedCount}</span>
            </span>
          )}
          {t.split && t.split.type !== "joint" && (
            <span
              className="transaction-indicator transaction-split"
              role="img"
              title={
                t.split.type === "equal"
                  ? "50/50 Split"
                  : t.split.type === "personal"
                    ? `Personal (${t.split.participants?.[0] || t.owner})`
                    : "Custom pool split"
              }
              aria-label={
                t.split.type === "equal"
                  ? "50/50 Split"
                  : t.split.type === "personal"
                    ? `Personal (${t.split.participants?.[0] || t.owner})`
                    : "Custom pool split"
              }
            >
              <Users size={12} strokeWidth={2} aria-hidden="true" />
            </span>
          )}
          {t.isSettlement && (
            <span
              className="transaction-indicator transaction-settlement"
              role="img"
              title="Partner debt settlement"
              aria-label="Partner debt settlement"
            >
              <Handshake size={12} strokeWidth={2} aria-hidden="true" />
            </span>
          )}
          {showTripBadge && Boolean(badgeTooltip) && (
            <span className="transaction-trip-wrap">
              <span
                role="button"
                tabIndex={0}
                className="transaction-trip-badge"
                title={badgeTooltip}
                aria-label={badgeTooltip}
                onClick={handleBadgeClick}
                onMouseEnter={handleMouseEnter}
                onMouseLeave={handleMouseLeave}
              >
                {badgeIcon}
              </span>
              {showTip && (
                <span className="transaction-trip-popover" role="tooltip">
                  {badgeIcon}
                  <span>{badgeTooltip}</span>
                </span>
              )}
            </span>
          )}
        </span>
        {t.pendingKey && <small className="sync-label">Not synced</small>}
        {t.needsReview ? (
          <small className="sync-label" style={{ color: "#ffd37a" }}>
            Needs review
          </small>
        ) : t.pending ? (
          <small className="sync-label">Pending at the bank</small>
        ) : null}
        <span className="transaction-meta">
          <span className="transaction-profile-icon" role="img" aria-label={t.owner} title={t.owner}>
            <Avatar owner={t.owner} size={14} />
          </span>{" "}
          {t.toAccountId ? (
            <>
              {account ? shortName(account.name) : ""}{" → "}
              {destinationOwner && (
                <span className="transaction-profile-icon" role="img" aria-label={destinationOwner} title={destinationOwner}>
                  <Avatar owner={destinationOwner} size={14} />
                </span>
              )}{" "}
              {shortName(destinationAccount?.name || "")}
            </>
          ) : (
            <>
              {showPayeeInMeta && (
                <>
                  <i>·</i> {payeeName}{" "}
                </>
              )}
              <i>·</i>{" "}
              {account ? shortName(account.name) : t.accountId} <i>·</i>{" "}
              {expense || income ? categoryLabel(t) : t.type}
              {markupFee !== null && (
                <>
                  {" "}<i>·</i> +{money(markupFee, displayCurrency)} markup
                </>
              )}
            </>
          )}
        </span>
      </span>
      <span className={"transaction-amount " + (income ? "income" : "")}>
        {showTotal ? (
          <span className="transaction-values">
            <small className="transaction-total" title={`Total ${money(baseAmount, displayCurrency)}`} aria-label={`Total ${money(baseAmount, displayCurrency)}`}>
              {money(baseAmount, displayCurrency).replace(` ${displayCurrency}`, "")} /
            </small>
            {primaryAmount}
          </span>
        ) : primaryAmount}
        <span>{displayCurrency}</span>
      </span>
    </button>
  );
}
